import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, ShoppingBag, Trash2 } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { useCart } from "../hooks/useCart";

// Common ISO alpha-2 destinations we've enumerated in the tariff. Anything
// not in this list falls back to the "ROW" band on the backend.
const COUNTRY_OPTIONS = [
  ["GB", "United Kingdom"],
  ["IE", "Ireland"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["ES", "Spain"],
  ["IT", "Italy"],
  ["NL", "Netherlands"],
  ["BE", "Belgium"],
  ["DK", "Denmark"],
  ["AT", "Austria"],
  ["SE", "Sweden"],
  ["FI", "Finland"],
  ["PL", "Poland"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["AU", "Australia (ROW)"],
  ["NZ", "New Zealand (ROW)"],
  ["OTHER", "Somewhere else (ROW)"],
];

const gbp = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function CartPage() {
  const { items, updateQty, remove, clear, count } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState({}); // id → product doc
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState(() => window.localStorage.getItem("last.ink_country") || "GB");
  const [shipping, setShipping] = useState(null);
  const [shippingErr, setShippingErr] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("");
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);

  // Persist chosen country so returning customers don't have to reselect.
  useEffect(() => {
    window.localStorage.setItem("last.ink_country", country);
  }, [country]);

  // Fetch every distinct product referenced in the cart. Deduped so we
  // don't hammer the API when a customer has 3 variants of one shirt.
  useEffect(() => {
    let alive = true;
    const uniquePids = Array.from(new Set(items.map((i) => i.product_id)));
    if (uniquePids.length === 0) {
      setProducts({});
      setLoading(false);
      return () => { alive = false; };
    }
    setLoading(true);
    Promise.all(
      uniquePids.map((pid) =>
        api.get(`/ink-products/${pid}`).then((r) => r.data).catch(() => null),
      ),
    ).then((results) => {
      if (!alive) return;
      const map = {};
      results.forEach((p, i) => {
        if (p) map[uniquePids[i]] = p;
      });
      setProducts(map);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [items]);

  useEffect(() => {
    api
      .get("/settings/ink-shop-checkout")
      .then((r) => setCheckoutEnabled(!!r.data?.enabled))
      .catch(() => setCheckoutEnabled(false));
  }, []);

  // Enrich cart lines with the actual product/variant details for
  // rendering. Lines whose product OR variant is no longer active are
  // pruned from view (the cart may still hold the entry until the
  // customer clicks "remove", so an admin who accidentally hides a
  // product doesn't lose the customer's whole basket).
  const lines = useMemo(() => {
    return items
      .map((it) => {
        const p = products[it.product_id];
        if (!p) return null;
        const v = (p.variants || []).find((x) => x.id === it.variant_id);
        if (!v) return null;
        return {
          product: p,
          variant: v,
          quantity: it.quantity,
          line_pence: Math.round((v.price || 0) * 100) * it.quantity,
        };
      })
      .filter(Boolean);
  }, [items, products]);

  const subtotalPence = lines.reduce((n, l) => n + l.line_pence, 0);

  // Recompute shipping quote whenever the basket or destination
  // changes. Debounced with an async guard so we don't render a stale
  // response.
  useEffect(() => {
    if (lines.length === 0) {
      setShipping(null);
      setShippingErr("");
      return undefined;
    }
    let alive = true;
    setShippingErr("");
    setShipping(null);
    api
      .post("/ink-shop/shipping-quote", {
        country: country === "OTHER" ? "XX" : country,
        items: items.map((it) => ({
          product_id: it.product_id,
          variant_id: it.variant_id,
          quantity: it.quantity,
        })),
      })
      .then((r) => {
        if (!alive) return;
        setShipping(r.data);
        // Default to cheapest available method.
        if (r.data.methods?.length) setSelectedMethod(r.data.methods[0].id);
      })
      .catch((e) => {
        if (!alive) return;
        setShippingErr(
          e?.response?.data?.detail ||
          "Couldn't calculate shipping right now. Try refreshing.",
        );
      });
    return () => { alive = false; };
  }, [items, country, lines.length]);

  const chosenMethod = shipping?.methods.find((m) => m.id === selectedMethod) || null;
  const shippingPence = chosenMethod?.price_pence || 0;
  const totalPence = subtotalPence + shippingPence;

  const goToCheckout = () => {
    // Step 2 wiring: pass the picked shipping method through the URL so
    // the /checkout page can create the Stripe session server-side.
    if (!checkoutEnabled) return;
    if (!chosenMethod) return;
    navigate(`/checkout?m=${encodeURIComponent(chosenMethod.id)}`);
  };

  const empty = lines.length === 0 && !loading;

  return (
    <div className="min-h-screen bg-[var(--brand-bg,#F5F7FA)] text-[var(--brand-ink)]" data-testid="cart-page">
      <Header />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <Link to="/shop" className="inline-flex items-center gap-1 text-xs text-[var(--brand-body)] hover:text-[var(--brand-ink)]" data-testid="cart-back">
          <ArrowLeft size={12} /> Keep shopping
        </Link>
        <h1 className="mt-3 font-display text-3xl font-black tracking-tight sm:text-4xl">
          Your cart {count > 0 ? <span className="text-[var(--brand-body)] text-2xl">· {count} item{count === 1 ? "" : "s"}</span> : null}
        </h1>

        {loading ? (
          <div className="mt-8 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[var(--brand-body)]" /></div>
        ) : empty ? (
          <div className="mt-8 rounded-md border border-dashed border-black/20 bg-white p-10 text-center" data-testid="cart-empty">
            <ShoppingBag className="mx-auto h-8 w-8 text-[var(--brand-body)]" />
            <p className="mt-3 font-display text-lg font-bold">Your cart is empty.</p>
            <Link to="/shop" className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-xs font-semibold text-white">
              Browse the shop
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            {/* Line items */}
            <ul className="space-y-3" data-testid="cart-lines">
              {lines.map((l) => (
                <li
                  key={`${l.product.id}:${l.variant.id}`}
                  className="flex items-start gap-4 rounded-md border border-black/10 bg-white p-4"
                  data-testid={`cart-line-${l.variant.id}`}
                >
                  <Link to={`/shop/${l.product.id}`} className="shrink-0">
                    <img src={l.variant.mockup || l.product.cover_image} alt="" className="h-20 w-20 rounded-md object-cover" />
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/shop/${l.product.id}`} className="font-display text-sm font-bold hover:underline">
                      {l.product.title}
                    </Link>
                    <p className="mt-0.5 text-xs text-[var(--brand-body)]">
                      {l.variant.label}
                    </p>
                    <div className="mt-3 flex items-center gap-3">
                      <label className="flex items-center gap-1 text-xs text-[var(--brand-body)]">
                        Qty
                        <input
                          type="number"
                          min={0}
                          max={50}
                          value={l.quantity}
                          onChange={(e) => updateQty(l.product.id, l.variant.id, e.target.value)}
                          className="w-16 rounded-sm border border-black/15 px-2 py-1 text-xs"
                          data-testid={`cart-qty-${l.variant.id}`}
                        />
                      </label>
                      <button
                        type="button"
                        onClick={() => remove(l.product.id, l.variant.id)}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"
                        data-testid={`cart-remove-${l.variant.id}`}
                      >
                        <Trash2 size={12} /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-display text-sm font-bold" data-testid={`cart-line-total-${l.variant.id}`}>
                      {gbp(l.line_pence)}
                    </p>
                    <p className="mt-0.5 text-[10px] text-[var(--brand-body)]">
                      £{l.variant.price.toFixed(2)} each
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <aside className="space-y-4">
              <div className="rounded-md border border-black/10 bg-white p-5">
                <h2 className="font-display text-sm font-bold">Delivery</h2>
                <label className="mt-3 block">
                  <span className="label-kicker text-[var(--brand-body)]">Ship to</span>
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    className="mt-1 w-full rounded-sm border border-black/15 bg-white px-2 py-1.5 text-sm outline-none focus:border-[#0A0F17]"
                    data-testid="cart-country"
                  >
                    {COUNTRY_OPTIONS.map(([code, label]) => (
                      <option key={code} value={code}>{label}</option>
                    ))}
                  </select>
                </label>

                {shippingErr ? (
                  <p className="mt-3 text-xs text-red-600" data-testid="cart-shipping-error">{shippingErr}</p>
                ) : !shipping ? (
                  <p className="mt-3 text-xs text-[var(--brand-body)]">Calculating shipping…</p>
                ) : (
                  <div className="mt-3 space-y-1.5" data-testid="cart-shipping-methods">
                    {shipping.methods.map((m) => (
                      <label
                        key={m.id}
                        className={`flex cursor-pointer items-start justify-between rounded-sm border p-2 text-xs ${
                          selectedMethod === m.id ? "border-[#0A0F17] bg-[#0A0F17]/5" : "border-black/10 hover:border-[#0A0F17]"
                        }`}
                      >
                        <span className="flex items-start gap-2">
                          <input
                            type="radio"
                            name="ship"
                            className="mt-0.5"
                            checked={selectedMethod === m.id}
                            onChange={() => setSelectedMethod(m.id)}
                            data-testid={`cart-ship-${m.id}`}
                          />
                          <span>
                            <span className="font-semibold">{m.label}</span>
                            <br />
                            <span className="text-[var(--brand-body)]">{m.estimate}</span>
                          </span>
                        </span>
                        <span className="font-semibold">{gbp(m.price_pence)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-md border border-black/10 bg-white p-5">
                <div className="flex items-center justify-between text-sm">
                  <span>Subtotal</span>
                  <span data-testid="cart-subtotal">{gbp(subtotalPence)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-sm">
                  <span>Shipping</span>
                  <span data-testid="cart-shipping-price">
                    {chosenMethod ? gbp(shippingPence) : "—"}
                  </span>
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-black/10 pt-3 font-display text-lg font-bold">
                  <span>Total</span>
                  <span data-testid="cart-total">{gbp(totalPence)}</span>
                </div>
                {checkoutEnabled ? (
                  <button
                    type="button"
                    onClick={goToCheckout}
                    disabled={!chosenMethod}
                    className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                    data-testid="cart-checkout"
                  >
                    <ShoppingBag size={14} /> Checkout
                  </button>
                ) : (
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      disabled
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-semibold text-white opacity-60"
                      data-testid="cart-checkout"
                    >
                      <ShoppingBag size={14} /> Checkout back soon
                    </button>
                    <p className="text-center text-xs text-[var(--brand-body)]">
                      In the meantime, the catalog is live for browsing.
                    </p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={clear}
                  className="mt-3 block w-full text-center text-xs text-[var(--brand-body)] hover:underline"
                  data-testid="cart-clear"
                >
                  Empty cart
                </button>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
